import { Info } from "lucide-react";
import type React from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import { Input } from "@krea/shared/design-system/input/input";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";

import { BANKID_TEST_IDS } from "../../test-constants";

interface SSNAuthFlowProps {
  ssn: string;
  onSSNChange: (ssn: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const SSNAuthFlow: React.FC<SSNAuthFlowProps> = ({
  ssn,
  onSSNChange,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const digitsAndHyphenValue = value.replace(/[^0-9-]/g, "");
    onSSNChange(digitsAndHyphenValue);
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    onSubmit();
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <FieldLabel htmlFor="bankid-ssn">
        <div className="flex items-center text-black">
          <span className="mr-1">{t("bankID.SE.ssn")}</span>
          <ResponsiveTooltip
            className="max-w-[300px]"
            trigger={<Info size={18} className="fill-gray-500 text-white" />}
          >
            {t("bankID.SE.ssnHelper")}
          </ResponsiveTooltip>
        </div>
      </FieldLabel>
      <Input
        type="text"
        pattern="[0-9]*"
        inputMode="numeric"
        value={ssn}
        onChange={onChange}
        placeholder={t("bankID.SE.ssnPlaceholder")}
        name="bankid-ssn"
        id="bankid-ssn"
        data-storage="false"
        autoComplete="off"
        data-test-id={BANKID_TEST_IDS.ssnInput}
      />

      <Button
        size="lg"
        variant={!ssn ? "secondary" : "default"}
        type="submit"
        className="mt-5 w-full"
        disabled={isSubmitting || !ssn}
        data-test-id={BANKID_TEST_IDS.bankIdContinueButton}
      >
        {t("bankID.common.continue")}
      </Button>
    </form>
  );
};
